import { UserAvatar } from "components/UserAvatar";
import { useBrand } from "hooks/useBrand";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import Paths from "routes/Paths";

import { navItems } from "../data";
import { IconName } from "../models";

import LineIcon from "./LineIcon";
import SidebarNotifications from "./SidebarNotifications";
import { useUnreadNotifications } from "./useUnreadNotifications";

import styles from "../HomePage.module.css";

/** Ties the phone toggle to the panel it expands. */
const navPanelId = "home-nav-panel";

type HomeSidebarProps = {
	displayName: string;
	initials: string;
	/** The signed-in user's profile photo; the initials render when absent. */
	profileImageUrl?: string | null;
	/** Active Pro subscribers see a membership badge instead of the upgrade button. */
	isPro: boolean;
	onSignOut: () => void;
	/** Opens the plans page. */
	onUpgrade: () => void;
};

type navItem = {
	icon: IconName;
	key: string;
	path?: string;
};

const HomeSidebar = ({ displayName, initials, isPro, onSignOut, onUpgrade, profileImageUrl }: HomeSidebarProps) => {
	const { t } = useTranslation("homePage");
	const { brand } = useBrand();
	const { pathname } = useLocation();
	const unreadCount = useUnreadNotifications();
	// Phones collapse the rail into a top bar; from tablet up the panel is always open (CSS decides).
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const toggleMenu = useCallback((): void => setIsMenuOpen((open: boolean) => !open), []);

	// Navigating away always lands on the next page with the menu closed.
	useEffect(() => {
		setIsMenuOpen(false);
	}, [pathname]);

	useEffect(() => {
		if (!isMenuOpen) {
			return undefined;
		}

		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === "Escape") {
				setIsMenuOpen(false);
			}
		};

		document.addEventListener("keydown", onKeyDown);

		return () => document.removeEventListener("keydown", onKeyDown);
	}, [isMenuOpen]);

	// The counted badge is hidden with the collapsed panel, so the toggle carries the unread signal.
	let menuLabel = t("homePage:sidebar.openMenu");

	if (isMenuOpen) {
		menuLabel = t("homePage:sidebar.closeMenu");
	} else if (unreadCount > 0) {
		menuLabel = t("homePage:sidebar.openMenuUnread");
	}

	return (
		<aside
			aria-label={t("homePage:sidebar.label")}
			className={`${styles.sidebar} ${isMenuOpen ? styles.sidebarOpen : ""}`}>
			<div className={styles.brand}>
				<img alt={brand} className={styles.logo} src={`${process.env.PUBLIC_URL}/assets/images/logo.png`} />
				<span className={styles.wordmark}>{brand}</span>

				<button
					aria-controls={navPanelId}
					aria-expanded={isMenuOpen}
					aria-label={menuLabel}
					className={styles.menuToggle}
					onClick={toggleMenu}
					type="button">
					<LineIcon name={isMenuOpen ? "close" : "menu"} />
					{!isMenuOpen && unreadCount > 0 && <span aria-hidden="true" className={styles.menuToggleDot} />}
				</button>
			</div>

			{/* `display: contents` above the phone breakpoint, so the desktop rail keeps its original layout. */}
			<div className={styles.sidebarPanel} id={navPanelId}>
				<div className={styles.profile}>
					<UserAvatar className={styles.profileAvatar} imageUrl={profileImageUrl} initials={initials} />
					<span className={styles.profileName}>{displayName}</span>
				</div>

				<nav className={styles.navList}>
					{navItems.map((item: navItem) => {
						const isActive = item.path !== undefined && item.path === pathname;

						return (
							<React.Fragment key={item.key}>
								<Link
									className={`${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
									to={item.path ?? Paths.root}>
									<LineIcon name={item.icon} />
									<span>{t(`homePage:navigation.${item.key}`)}</span>
								</Link>
								{item.key === "explore" && <SidebarNotifications unreadCount={unreadCount} />}
							</React.Fragment>
						);
					})}
				</nav>

				<div className={styles.sidebarFooter}>
					{isPro ? (
						<div className={styles.proMemberBadge}>
							<LineIcon name="sparkle" />
							<span>{t("homePage:sidebar.proMember")}</span>
						</div>
					) : (
						<button className={styles.upgradeButton} onClick={onUpgrade} type="button">
							<LineIcon name="sparkle" />
							<span>{t("homePage:sidebar.upgradeToPro")}</span>
						</button>
					)}
					<button className={styles.logoutButton} onClick={onSignOut} type="button">
						<LineIcon name="logout" />
						<span>{t("homePage:navigation.logout")}</span>
					</button>

					<nav aria-label={t("homePage:sidebar.legalLinksLabel")} className={styles.legalLinks}>
						<Link to={Paths.helpSupport}>{t("homePage:navigation.helpSupport")}</Link>
						<Link to={Paths.privacyPolicy}>{t("homePage:sidebar.privacy")}</Link>
						<Link to={Paths.termsOfService}>{t("homePage:sidebar.terms")}</Link>
						<Link to={Paths.cookiePolicy}>{t("homePage:sidebar.cookies")}</Link>
					</nav>
				</div>
			</div>
		</aside>
	);
};

export default HomeSidebar;
